"use client"

import { useSyncExternalStore, useCallback, useEffect } from "react"
import type { SectionSlug } from "@/lib/types/sections"
import { FF_ALWAYS_ADVISOR_MODE } from "@/lib/config/feature-flags"

export type ChatMode = "profile" | "advisor"

interface ChatModeState {
  mode: ChatMode
  activeSection: SectionSlug | null
  context: Record<string, unknown>
  memoryCount: number
  keyDetailsCompleted: number
}

let state: ChatModeState = {
  mode: FF_ALWAYS_ADVISOR_MODE ? "advisor" : "profile",
  activeSection: null,
  context: {},
  memoryCount: 0,
  keyDetailsCompleted: 0,
}

const listeners = new Set<() => void>()

function emitChange() {
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return state
}

function getServerSnapshot(): ChatModeState {
  return { mode: "profile", activeSection: null, context: {}, memoryCount: 0, keyDetailsCompleted: 0 }
}

// Actions
function setMode(mode: ChatMode) {
  if (FF_ALWAYS_ADVISOR_MODE && mode === "profile") {
    console.log("[v0] Blocked mode switch to profile - FF_ALWAYS_ADVISOR_MODE is enabled")
    return
  }
  state = { ...state, mode }
  emitChange()
}

function setActiveSection(section: SectionSlug | null) {
  state = { ...state, activeSection: section }
  emitChange()
}

function setContext(context: Record<string, unknown>) {
  state = { ...state, context }
  emitChange()
}

function enterSectionMode(section: SectionSlug) {
  state = { ...state, activeSection: section }
  emitChange()
}

function exitSectionMode() {
  state = { ...state, activeSection: null }
  emitChange()
}

function incrementMemoryCount() {
  state = { ...state, memoryCount: state.memoryCount + 1 }
  emitChange()
}

function setMemoryCount(count: number) {
  state = { ...state, memoryCount: count }
  emitChange()
}

function incrementKeyDetails() {
  state = { ...state, keyDetailsCompleted: Math.min(5, state.keyDetailsCompleted + 1) }
  emitChange()
}

function setKeyDetailsCompleted(count: number) {
  state = { ...state, keyDetailsCompleted: count }
  emitChange()
}

export const chatModeStoreActions = {
  setMode,
  setActiveSection,
  setContext,
  enterSectionMode,
  exitSectionMode,
  incrementMemoryCount,
  setMemoryCount,
  incrementKeyDetails,
  setKeyDetailsCompleted,
  getState: () => state,
}

export function useChatModeStore() {
  const currentState = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  useEffect(() => {
    if (FF_ALWAYS_ADVISOR_MODE && currentState.mode !== "advisor") {
      setMode("advisor")
    }
  }, [currentState.mode])

  return {
    mode: currentState.mode,
    activeSection: currentState.activeSection,
    context: currentState.context,
    memoryCount: currentState.memoryCount ?? 0,
    keyDetailsCompleted: currentState.keyDetailsCompleted ?? 0,
    setMode: useCallback(setMode, []),
    setActiveSection: useCallback(setActiveSection, []),
    setContext: useCallback(setContext, []),
    enterSectionMode: useCallback(enterSectionMode, []),
    exitSectionMode: useCallback(exitSectionMode, []),
    incrementMemoryCount: useCallback(incrementMemoryCount, []),
    setMemoryCount: useCallback(setMemoryCount, []),
    incrementKeyDetails: useCallback(incrementKeyDetails, []),
    setKeyDetailsCompleted: useCallback(setKeyDetailsCompleted, []),
  }
}
